/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import styled, { css } from 'styled-components';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import Loading from '../../../commonComponents/Loading';
import ScreenBottomMessageBar from '../../common/ScreenBottomMessageBar';
import { isLoggedIn } from '../../../core/localStorage';
import { StyledGrid } from '../../Styles';
import Content from './Content';
import {
	blackColour,
	borderGrey,
	bottomNavBarV2Height,
	brandingColourHeavy,
	globalHeaderZIndex,
	tabletLandscapePadding,
	lightGreyColour,
	newContentPadding,
	tabletLandscapeMinWidth,
	tabletPortraitMinWidth,
	borderLightGrey,
	greyBackground,
	separatorLineGrey,
	sectionPadding,
	tabMenuHeight
} from '../../cssConstants';
import AutoLogout from '../../common/AutoLogout';
import Footer from './Footer';
import Header from './Header';
import { dashboardPageUrl } from '../../../core/constants';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import { isAndroidApp, isWebApp, pixelsToNumber, scrollToPageTop } from '../../../lib/utils';
import useLoadingStore from '../../../zustandStore/loadingStateStore';
import useSoftKeyboardHeightStore from '../../../zustandStore/softKeyboardHeightStore';

export const Layout = React.forwardRef<HTMLDivElement, Props>((props, _ref) => {
	const {
		children,
		onBackButtonClick = undefined,
		showHeader = true,
		showFooter = true,
		headerText = '',
		networkUserProfileIconShown = false,
		currentStep = 0,
		totalStep = 0,
		colour = '',
		shouldScrollToTopForSoftKeyboard = false,
		widerView = false,
		scrollResetDependency,
		rightAction
	} = props;

	const navigate = useNavigateWithAppName();
	const { pathname } = useLocation();
	const loading = useLoadingStore((state) => state.loading);
	const { softKeyboardHeight, setSoftKeyboardHeight } = useSoftKeyboardHeightStore();
	const containerRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isAndroidApp) setStatusBarForBackground('dark');
	}, []);

	useEffect(() => {
		let showListener: PluginListenerHandle;
		let hideListener: PluginListenerHandle;

		if (!isWebApp) {
			const setupKeyboardListeners = async () => {
				showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
					setSoftKeyboardHeight(info.keyboardHeight);

					if (shouldScrollToTopForSoftKeyboard) {
						setTimeout(() => {
							window.scrollTo({
								top: document.body.scrollHeight,
								behavior: 'smooth'
							});
						}, 0);
					}
				});

				hideListener = await Keyboard.addListener('keyboardWillHide', () => {
					setSoftKeyboardHeight(0);
				});
			};

			setupKeyboardListeners();
		}

		return () => {
			showListener?.remove();
			hideListener?.remove();
		};
	}, [shouldScrollToTopForSoftKeyboard]);

	useEffect(() => {
		scrollToPageTop();
	}, [pathname]);

	// Scroll to top when scrollResetDependency changes (e.g., when switching tabs)
	useEffect(() => {
		if (scrollResetDependency !== undefined && containerRef.current) {
			containerRef.current.scrollIntoView({ behavior: 'auto' });
		}
	}, [scrollResetDependency]);

	const handleDefaultGoBackClick = () => {
		if (window.history.length > 1) {
			navigate(-1);
		} else {
			navigate(dashboardPageUrl);
		}
	};

	return (
		<PageContainer
			ref={containerRef}
			$bottomGapForSoftKeyboard={softKeyboardHeight}
			$shouldScrollToTopForSoftKeyboard={shouldScrollToTopForSoftKeyboard}
		>
			{showHeader && (
				<Header
					headerText={headerText}
					onBackButtonClick={onBackButtonClick === undefined ? handleDefaultGoBackClick : onBackButtonClick}
					networkUserProfileIconShown={networkUserProfileIconShown}
					currentStep={currentStep}
					totalStep={totalStep}
					colour={colour}
					showProgressLine={headerText && totalStep > 0}
					rightAction={rightAction}
				/>
			)}

			<Content
				widerView={widerView}
				showHeader={showHeader}
				showFooter={showFooter}
				headerHasProgressLine={headerText && totalStep > 0}
			>
				{children}
			</Content>
			{showFooter && <Footer />}
			<ScreenBottomMessageBar />
			{loading && <Loading />}
		</PageContainer>
	);
});

const InnerLayout = React.forwardRef<HTMLDivElement, Props>((props, ref) => (
	<Layout {...defaultProps} {...props} ref={ref}>
		{props.children}
	</Layout>
));

// Only auto logout for web app as user's phone will lock out screen after a few minutes,
// so the mobile app will stay open and be inactive, so would auto logout mobile app users always (refresh token expires in 2 hours).
const AutoLogoutLayout = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
	return isLoggedIn() && !isMobile ? (
		<AutoLogout>
			<InnerLayout {...defaultProps} {...props} ref={ref} />
		</AutoLogout>
	) : (
		<InnerLayout {...defaultProps} {...props} ref={ref} />
	);
});

export default AutoLogoutLayout;

interface Props {
	children: React.ReactNode;
	onBackButtonClick?: (() => void) | null | undefined;
	showHeader?: boolean;
	showFooter?: boolean;
	headerText?: string;
	networkUserProfileIconShown?: boolean;
	currentStep?: number;
	totalStep?: number;
	colour?: string;
	shouldScrollToTopForSoftKeyboard?: boolean;
	widerView?: boolean;
	scrollResetDependency?: unknown;
	rightAction?: React.ReactNode;
}

const defaultProps = {
	onBackButtonClick: undefined as unknown as (() => void) | null | undefined,
	showHeader: true,
	showFooter: true,
	headerText: '',
	currentStep: 0,
	totalStep: 0,
	colour: '',
	shouldScrollToTopForSoftKeyboard: false // By default, don't push up viewports when soft keyboard is shown
};

const getPaddingBottom = (bottomGapForSoftKeyboard: number, shouldScrollToTopForSoftKeyboard: boolean) => {
	let bottomNavBarHeight = pixelsToNumber(bottomNavBarV2Height);
	if (!isLoggedIn() || (shouldScrollToTopForSoftKeyboard && bottomGapForSoftKeyboard > 0)) {
		// Suppose not want to show bottom nav bar for pages that need to scroll to top to show input field above
		// the soft keyboard, eg. chat page, comments page
		bottomNavBarHeight = 0;
	}
	const paddingBottom = pixelsToNumber(newContentPadding);
	return bottomGapForSoftKeyboard + paddingBottom + bottomNavBarHeight;
};

const PageContainer = styled.div<{
	$bottomGapForSoftKeyboard: number;
	$shouldScrollToTopForSoftKeyboard: boolean;
}>`
	min-height: calc(100vh - ${(props) => `${props.$bottomGapForSoftKeyboard}px`});
	background-color: ${greyBackground};
	padding: 0 ${newContentPadding};
	padding-bottom: ${({ $bottomGapForSoftKeyboard, $shouldScrollToTopForSoftKeyboard }) => {
		const basePadding = getPaddingBottom($bottomGapForSoftKeyboard, $shouldScrollToTopForSoftKeyboard);
		return isAndroidApp ? `calc(${basePadding}px + var(--safe-area-inset-bottom))` : `${basePadding}px`;
	}};
	overflow-y: auto;

	@media (min-width: ${tabletLandscapeMinWidth}) {
		padding-left: ${tabletLandscapePadding};
		padding-right: ${tabletLandscapePadding};
	}
`;

interface TitleParagraphProps {
	color?: string;
	$bold?: boolean;
}

export const PageContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${sectionPadding};
`;

export const TabContent = styled(PageContent)`
	${PageContent};
	padding-top: ${tabMenuHeight};
	margin: ${sectionPadding} 0;
`;

export const TitleParagraph = styled.p<TitleParagraphProps>`
	text-align: center;
	font-size: 20px;
	margin: 20px 0 0 0;
	font-weight: ${({ $bold }) => ($bold ? 500 : 'normal')};
	color: ${({ color }) => color || brandingColourHeavy};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 35px;
	}
`;

export const SubTitleParagraph = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: ${lightGreyColour};
	letter-spacing: 0.25px;
	padding: 10px 0 5px 0;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 18px;
		line-height: 24px;
	}
`;

export const StyledForm = styled.form`
	display: grid;

	@media (min-width: ${tabletPortraitMinWidth}) {
		gap: 15px;
	}
`;

export const StyledFormDiv = styled.div`
	width: 100%;
	margin-right: 0;
	display: flex;
	flex-wrap: wrap;

	@media (min-width: ${tabletPortraitMinWidth}) {
		justify-content: center;
		flex-wrap: nowrap;
	}

	> div {
		@media (min-width: ${tabletPortraitMinWidth}) {
			width: calc(33.33% - 10px);

			&:nth-child(1),
			&:nth-child(2) {
				margin-right: 20px; /* Or any value you want */
			}

			&:nth-child(3n) {
				margin-right: 0;
			}
		}
	}
`;

export const StyledGridContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;

	.k-grid-toolbar {
		background: none;
		padding-left: 0;
		border-width: 1px 0;
		border-color: ${borderGrey};
		border-opacity: 0.5;
	}

	div.k-grid-norecords {
		width: auto !important;
	}

	.k-grid-norecords-template {
		border: 0px;
	}
`;

export const StyledGridWrapper = styled(StyledGrid)`
	thead.k-table-thead {
		background: none;
		font-weight: 500;
		font-size: 14px;

		@media (min-width: ${tabletPortraitMinWidth}) {
			font-size: 16px;
		}
	}
`;

export const StyledLabel = styled.div`
	padding: 10px 0 10px 0;
	font-size: 16px;
	color: ${blackColour};
`;

export const ContentLogoWrapper = styled.section`
	top: 0;
	width: 100%;
	z-index: ${globalHeaderZIndex};
	text-align: center;
	padding: 15px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		padding: 30px;
	}
`;

export const ContentLogo = styled.img`
	height: auto;
	width: 100px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		width: 120px;
	}
`;

export const SeparatorLineBase = css`
	width: 100%;
	height: 2px;
	background-color: ${separatorLineGrey};
	position: relative;
`;

export const SeparatorLine = styled.div`
	${SeparatorLineBase};
	margin-bottom: ${sectionPadding};
`;

export const MarginSeparatorLine = styled.div`
	${SeparatorLineBase};
	margin: ${sectionPadding} 0;
`;

export const CardSeparatorLine = styled.div`
	background: ${brandingColourHeavy};
	height: 1px !important;
`;

export const EventImg = styled.img`
	width: 100%;
	border-radius: 14px;
	overflow: hidden;
	object-fit: cover;
	border: 2px solid ${borderLightGrey};
	display: block;
`;

export const StyledVideoWrapperBase = css`
	width: 100%;
	aspect-ratio: 16/9;
	border-radius: 12px;
	overflow: hidden;
	position: relative;
`;

export const StyledImgAndVideo = css`
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 12px;
	position: absolute;
	top: 0;
	left: 0;
	transition: opacity 0.5s ease;
`;

export const StyledBackgroundImage = styled.img<{ $isHidden: boolean }>`
	${StyledImgAndVideo};
	opacity: ${(props) => (props.$isHidden ? 0 : 1)};
`;

export const StyledVideo = styled.video<{ $isPlaying: boolean }>`
	${StyledImgAndVideo};
	opacity: ${(props) => (props.$isPlaying ? 1 : 0)};
`;

export const setStatusBarForBackground = async (bgColor: 'light' | 'dark') => {
	if (bgColor === 'light') {
		await StatusBar.setStyle({ style: Style.Dark });
	} else {
		await StatusBar.setStyle({ style: Style.Light });
	}
};
